<template>
  <div class="space-y-3">
     <div>
      <label :for="`update-lienholder-name-${_uid}`" class="block text-sm font-medium text-gray-700">Lienholder Name <span class="text-red-500">*</span></label>
      <input 
        type="text" 
        :id="`update-lienholder-name-${_uid}`" 
        :value="modelValue.lienholderName"
        @input="updateDetail('lienholderName', $event.target.value)"
        class="input-responsive"
        required
        aria-describedby="update-lienholder-name-error"
      />
      <p v-if="errors.lienholderName" :id="`update-lienholder-name-error`" class="error-message">{{ errors.lienholderName }}</p>
    </div>
     <div>
      <label :for="`update-lienholder-loan-${_uid}`" class="block text-sm font-medium text-gray-700">Loan Number <span class="text-red-500">*</span></label>
      <input 
        type="text" 
        :id="`update-lienholder-loan-${_uid}`" 
        :value="modelValue.loanAccountNumber"
        @input="updateDetail('loanAccountNumber', $event.target.value)"
        class="input-responsive"
        required
        aria-describedby="update-lienholder-loan-error"
      />
      <p v-if="errors.loanAccountNumber" :id="`update-lienholder-loan-error`" class="error-message">{{ errors.loanAccountNumber }}</p>
    </div>
     <div>
      <label :for="`update-lienholder-address-${_uid}`" class="block text-sm font-medium text-gray-700">Lienholder Full Address <span class="text-red-500">*</span></label>
      <textarea 
        :id="`update-lienholder-address-${_uid}`" 
        rows="3" 
        :value="modelValue.lienholderAddress"
        @input="updateDetail('lienholderAddress', $event.target.value)"
        placeholder="Street, City, State, ZIP"
        class="input-responsive"
        required
        aria-describedby="update-lienholder-address-error"
      ></textarea>
      <p v-if="errors.lienholderAddress" :id="`update-lienholder-address-error`" class="error-message">{{ errors.lienholderAddress }}</p>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, getCurrentInstance } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

// Get unique ID for labels/inputs
const instance = getCurrentInstance();
const _uid = instance ? instance.uid : Math.random().toString(36).substring(7);

const props = defineProps({
  modelValue: { 
    type: Object,
    required: true,
    default: () => ({ lienholderName: '', loanAccountNumber: '', lienholderAddress: '' }) 
  }
});

const emit = defineEmits(['update:modelValue']);

// --- Internal Validation Setup ---
const validationSchema = yup.object({
  lienholderName: yup.string().required('Lienholder name is required.'),
  lienholderAddress: yup.string().required('Lienholder address is required.'),
  loanAccountNumber: yup.string().required('Loan/Account number is required.'),
});

const { errors, validate: validateInternally, setErrors } = useForm({
  validationSchema: validationSchema,
  initialValues: props.modelValue 
});

const updateDetail = (key, value) => {
  const newValue = { 
    ...props.modelValue, 
    [key]: value 
  };
  emit('update:modelValue', newValue);
};

// --- Expose validation function to parent ---
defineExpose({
  validate() {
    let isValid = true;
    const currentErrors = {};

    // Perform primary synchronous checks for required fields
    if (!props.modelValue.lienholderName || !props.modelValue.lienholderName.trim()) {
      isValid = false;
      currentErrors.lienholderName = 'Lienholder name is required.';
    }
    if (!props.modelValue.loanAccountNumber || !props.modelValue.loanAccountNumber.trim()) {
      isValid = false;
      currentErrors.loanAccountNumber = 'Loan/Account number is required.';
    }
    if (!props.modelValue.lienholderAddress || !props.modelValue.lienholderAddress.trim()) {
      isValid = false;
      currentErrors.lienholderAddress = 'Lienholder address is required.';
    }

    // Trigger VeeValidate's internal validation
    validateInternally({ force: true });

    // If our direct synchronous checks already failed, return false.
    if (!isValid) {
      setErrors({
        ...errors.value, // Preserve other errors VeeV might have set
        ...currentErrors 
      });
      return false;
    }

    // After triggering VeeValidate and passing initial synchronous checks,
    // check the reactive 'errors' object for any errors VeeValidate might have found.
    return Object.keys(errors.value).length === 0;
  }
});
</script>

<style scoped>
.input-responsive {
    @apply mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm;
}
.error-message {
    @apply text-red-500 text-xs italic mt-1;
}
</style> 